import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError, useAuth, usePermission } from '@saas-kit/lib';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  PageHeader,
  toast,
} from '@saas-kit/ui';

import { AddMemberForm } from './AddMemberForm';
import { listInvitations, revokeInvitation, type Invitation } from './backendApi';
import { TenantRequiredState } from '../../components/TenantRequiredState';

export function BackendInvitationsPage(): JSX.Element {
  const { t } = useTranslation('invitations');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canInvite = usePermission('members.invite');
  const canManage = usePermission('members.manage');
  const queryClient = useQueryClient();
  const [pendingRevoke, setPendingRevoke] = useState<Invitation | null>(null);

  const invitesQuery = useQuery({
    queryKey: ['tenant-invitations', selectedTenant?.tenant_id],
    queryFn: listInvitations,
    enabled: Boolean(selectedTenant?.tenant_id),
  });

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(invitationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenant-invitations'] });
      setPendingRevoke(null);
      toast.success(t('toasts.revoked'));
    },
    onError: (error) => toast.error(t(parseApiError(error).i18nKey)),
  });

  const invitations = invitesQuery.data ?? [];
  const nearestExpiration = invitations
    .map((invitation) => invitation.expires_at)
    .sort()[0];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        actions={
          <>
            <Link to="/team">
              <Button variant="secondary">{t('actions.open_members')}</Button>
            </Link>
            {canManage ? (
              <Link to="/team/roles">
                <Button>{t('actions.open_roles')}</Button>
              </Link>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <MetricCard label={t('metrics.pending')} value={String(invitations.length)} />
        <MetricCard
          label={t('metrics.next_expiry')}
          value={nearestExpiration ? formatDate(nearestExpiration) : '—'}
        />
      </div>

      {!selectedTenant ? (
        <TenantRequiredState
          title={t('tenant_required.title')}
          body={t('tenant_required.body')}
          cta={t('tenant_required.cta')}
        />
      ) : !canInvite ? (
        <ErrorBanner>{t('errors.invite_denied')}</ErrorBanner>
      ) : (
        <>
          <AddMemberForm />

          <Card>
            <CardHeader>
              <CardTitle>{t('list.section_title')}</CardTitle>
              <CardDescription>{t('list.section_body')}</CardDescription>
            </CardHeader>
            <CardContent>
              {invitesQuery.isError ? (
                <ErrorBanner>{t(parseApiError(invitesQuery.error).i18nKey)}</ErrorBanner>
              ) : invitations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="border-b border-border text-start text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">{t('list.columns.email')}</th>
                        <th className="px-3 py-2 font-medium">{t('list.columns.role')}</th>
                        <th className="px-3 py-2 font-medium">{t('list.columns.expires_at')}</th>
                        <th className="px-3 py-2 font-medium">{t('list.columns.issued_at')}</th>
                        <th className="px-3 py-2 font-medium">{t('list.columns.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.map((invitation) => (
                        <tr key={invitation.id} className="border-b border-border/70">
                          <td className="px-3 py-3">{invitation.email}</td>
                          <td className="px-3 py-3">
                            <Badge variant="outline">{t(`roles.${invitation.role}`)}</Badge>
                          </td>
                          <td className="px-3 py-3">{formatDate(invitation.expires_at)}</td>
                          <td className="px-3 py-3">{formatDate(invitation.created_at)}</td>
                          <td className="px-3 py-3">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => setPendingRevoke(invitation)}
                            >
                              {t('actions.revoke')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : invitesQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">{t('loading')}</p>
              ) : (
                <EmptyState
                  title={t('list.empty_title')}
                  description={t('list.empty')}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {pendingRevoke ? (
        <ConfirmDialog
          title={t('confirm.revoke.title', { email: pendingRevoke.email })}
          body={t('confirm.revoke.body', { email: pendingRevoke.email })}
          confirmLabel={t('actions.revoke')}
          cancelLabel={t('cancel')}
          variant="destructive"
          isBusy={revokeMutation.isPending}
          onCancel={() => setPendingRevoke(null)}
          onConfirm={() => revokeMutation.mutate(pendingRevoke.id)}
        />
      ) : null}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}
